import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { IndianRupee, ShoppingBag, Users, Package, Percent, Store, Clock, CheckCircle2, ArrowRight } from "lucide-react";

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    Promise.all([api.get("/admin/stats"), api.get("/admin/orders")]).then(([s, o]) => {
      setStats(s.data);
      setOrders(o.data);
    });
  }, []);

  if (!stats) return <div className="p-10 text-center text-[#3B5249]">Loading…</div>;

  const cards = [
    { icon: IndianRupee, label: "GMV", value: `₹${stats.gmv.toLocaleString()}` },
    { icon: Percent, label: "Commission (1%)", value: `₹${stats.commission.toLocaleString()}` },
    { icon: ShoppingBag, label: "Orders", value: stats.orders_total },
    { icon: Users, label: "Buyers", value: stats.buyers },
    { icon: Store, label: "Vendors (approved)", value: `${stats.vendors_approved}/${stats.vendors_total}` },
    { icon: Package, label: "Products (approved)", value: `${stats.products_approved}/${stats.products_total}` },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="text-xs uppercase tracking-[0.2em] text-[#14A085]">Admin</div>
      <h1 className="font-display text-3xl md:text-4xl text-[#0F3D2E]">Marketplace Overview</h1>

      <div className="mt-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4" data-testid="admin-stats">
        {cards.map((c, i) => (
          <div key={i} className="rounded-xl border border-[#E2E8E5] bg-white p-5">
            <c.icon size={20} className="text-[#14A085]" />
            <div className="mt-2 text-xs text-[#6E877C] uppercase tracking-widest">{c.label}</div>
            <div className="mt-1 font-display text-2xl text-[#0F3D2E]" data-testid={`admin-stat-${i}`}>{c.value}</div>
          </div>
        ))}
      </div>

      <div className="mt-8 grid md:grid-cols-4 gap-4">
        <Link to="/admin/vendors" className="card-hover rounded-xl border border-[#E2E8E5] bg-white p-6 flex items-center justify-between" data-testid="link-admin-vendors">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-[#14A085]">Review</div>
            <div className="font-display text-xl text-[#0F3D2E] mt-1">Vendor Applications</div>
            <div className="text-sm text-[#3B5249] mt-1">{stats.vendors_pending} pending · {stats.vendors_approved} approved</div>
          </div>
          <ArrowRight className="text-[#14A085]" />
        </Link>
        <Link to="/admin/products" className="card-hover rounded-xl border border-[#E2E8E5] bg-white p-6 flex items-center justify-between" data-testid="link-admin-products">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-[#14A085]">Review</div>
            <div className="font-display text-xl text-[#0F3D2E] mt-1">Product Submissions</div>
            <div className="text-sm text-[#3B5249] mt-1">{stats.products_pending} pending approval</div>
          </div>
          <ArrowRight className="text-[#14A085]" />
        </Link>
        <Link to="/admin/settlements" className="card-hover rounded-xl border border-[#E2E8E5] bg-white p-6 flex items-center justify-between" data-testid="link-admin-settlements">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-[#14A085]">Finance</div>
            <div className="font-display text-xl text-[#0F3D2E] mt-1">Vendor Settlements</div>
            <div className="text-sm text-[#3B5249] mt-1">Payables & commission reports</div>
          </div>
          <ArrowRight className="text-[#14A085]" />
        </Link>
        <Link to="/admin/messages" className="card-hover rounded-xl border border-[#E2E8E5] bg-white p-6 flex items-center justify-between" data-testid="link-admin-messages">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-[#14A085]">Support</div>
            <div className="font-display text-xl text-[#0F3D2E] mt-1">Contact Messages</div>
            <div className="text-sm text-[#3B5249] mt-1">Enquiries from the public contact form</div>
          </div>
          <ArrowRight className="text-[#14A085]" />
        </Link>
      </div>

      <div className="mt-8 rounded-xl border border-[#E2E8E5] bg-white p-6">
        <h2 className="font-display text-xl text-[#0F3D2E] mb-4">Recent Orders</h2>
        {orders.length === 0 ? (
          <p className="text-sm text-[#3B5249]">No orders yet.</p>
        ) : (
          <div className="overflow-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-[#6E877C] uppercase tracking-widest">
                  <th className="py-2">Order</th><th>Items</th><th>Total</th><th>Commission</th><th>Payment</th><th>Placed</th>
                </tr>
              </thead>
              <tbody>
                {orders.slice(0, 15).map((o) => (
                  <tr key={o.id} className="border-t border-[#E2E8E5]">
                    <td className="py-2 font-mono text-xs">#{o.id.slice(-8).toUpperCase()}</td>
                    <td>{o.items.length}</td>
                    <td className="font-medium text-[#0F3D2E]">₹{o.total.toLocaleString()}</td>
                    <td className="text-[#14A085]">₹{o.commission_total.toLocaleString()}</td>
                    <td className="capitalize">{o.payment_method?.replace("_"," ")}</td>
                    <td className="text-xs text-[#6E877C]">{new Date(o.created_at).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
